// eslint-disable-next-line simple-import-sort/imports, import/order
import ArticleEditor from './imperavi/article-editor';
import './BjxArticleEditor.scss';
import './imperavi/css/article-editor.css';
import './imperavi/plugins/inlineformat/inlineformat';
import './imperavi/plugins/blockcode/blockcode';
import './imperavi/plugins/buttonlink/buttonlink';
import './imperavi/plugins/counter/counter';
import './imperavi/plugins/definedlinks/definedlinks';
import './imperavi/plugins/reorder/reorder';
import './imperavi/plugins/removeformat/removeformat';
import './imperavi/plugins/underline/underline';
import './imperavi/plugins/imageresize/imageresize';
import './imperavi/plugins/imageposition/imageposition';

import React, { useContext, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { AppContext } from 'components/AppContext/AppContext';
import { useImageUploader } from 'customHooks/useImageUploader';
import { Link } from 'models/Link';
import { v4 as uuidv4 } from 'uuid';
import { LogLevel } from 'models/LogLevel';
import { EditorInterface, TourContext } from 'data/intros/TourContext';



// import ArticleEditor from './imperavi/article-editor';

const instanceId = `editor-${uuidv4()}`;

export interface EditorProps {
  predefinedLinks: Link[];
  // eslint-disable-next-line react/require-default-props
  placeholderText?: string;
  // eslint-disable-next-line react/require-default-props
  content?: string;
  onEditorChange: (value: string) => void;
  tabIndex: number | undefined;
}

export interface ReRenderAction {
  reRender: () => void;
}

const BjxArticleEditor: React.ForwardRefRenderFunction<ReRenderAction, EditorProps> = ({
  predefinedLinks,
  placeholderText,
  content,
  onEditorChange,
  tabIndex,
}, ref) => {
  const appReference = useRef<any>();
  const [hasRendered, setHasRendered] = useState(false);
  const { configRepo, selectedProjectId } = useContext(AppContext);
  const { handleUploadFromEditor } = useImageUploader(configRepo!.SCHEDULE_API_BASE_URI);
  const { sendLog } = useContext(AppContext);

  const plugins = [
    'blockcode',
    'buttonlink',
    'counter',
    'definedlinks',
    'imageresize',
    'imageposition',
    'inlineformat',
    'reorder',
    'removeformat',
    'underline',
  ];
  const format = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'ul', 'ol', 'dl', 'address'];
  const subscribe = {
    'editor.change': (event: any) => {
      onEditorChange(event.params.html);
    },
  };
  const inlineformat = {
    items: ['sup', 'sub', 'mark', 'code', 'kbd'],
  };

  useEffect(() => {
    appReference.current = ArticleEditor(`#${instanceId}`, {
      css: '/css/',
      plugins,
      placeholder: placeholderText,
      format,
      subscribe,
      inlineformat,
      buttonlink: {
        classname: 'button button-primary',
      },
      definedlinks: {
        items: predefinedLinks,
      },
      // see: https://imperavi.com/article/docs/upload-images/
      image: {
        upload: handleUploadFromEditor,
        multiple: false,
        data: {
          projectId: selectedProjectId,
        },
      },
    });

    setHasRendered(true);

    const tourContext = (window as any as TourContext<EditorInterface>);
    if (!tourContext.args) {
      tourContext.args = {};
    }
    tourContext.args.articleEditor = appReference.current;

    return () => {
      try {
        appReference.current?.destroy();
      }
      catch (er: any) {
        sendLog(`Destroying article rich editor failed with ${er.message}`, LogLevel.error);
      }
    };
  }, []);

  const reRender = () => {
    setHasRendered(true);
  };
  useImperativeHandle(ref, () => ({ reRender }));

  useEffect(() => {
    if (hasRendered) {
      appReference.current.editor.setContent({ html: content });
      setHasRendered(false);
    }
  }, [content, hasRendered]);

  return <textarea id={instanceId} tabIndex={tabIndex} defaultValue={content} />;
};

export default React.forwardRef(BjxArticleEditor);
